import { unifiedCameraUtils } from './uniCamUtils'
import { useCameraStore } from '@renderer/stores/cameraStore'

type CameraKey = 'primary' | 'secondary'

interface Config {
  name: string
  info: string
  label: string
  readonly: boolean
  type: 'range' | 'menu' | 'radio' | 'toggle' | 'text' | 'window' | 'section' | 'datetime'
  value?: string | number | boolean
  min?: number
  max?: number
  step?: number
  choices?: string[]
  children?: Record<string, Config>
}

export interface OriginalSettings {
  shutterSpeed?: {
    name: string
    value: string | number | boolean
  }
  exposureSimulation?: {
    name: string
    value: string | number | boolean
  }
}

export interface AvailableSettings {
  shutterSpeed?: string
  exposureSimulation?: string
  isSupported: boolean
}

// Default values
const DEFAULT_LIVE_VIEW_SHUTTER_SPEED = 1 / 30 // 1/30s in seconds
const MAX_FLASH_SYNC_SPEED = 1 / 200 // 1/200s - typical flash sync speed limit
const MIN_SHUTTER_SPEED = 1 / 4 // 1/4s - prevent too slow (motion blur)

// Common shutter speed setting names across different camera manufacturers
const SHUTTER_SPEED_NAMES = [
  'shutterspeed',
  'shutterspeed2',
  'shutterspeedvalue',
  'shutterspeedraw',
  'shutterspeedtv'
]

// Common exposure simulation setting names
const EXPOSURE_SIMULATION_NAMES = [
  'exposuresimulation',
  'exposuresim',
  'viewfinder',
  'liveviewsimulation',
  'simulation'
]

// Store original settings per camera key for restoration
const originalSettingsCache: Record<CameraKey, OriginalSettings | null> = {
  primary: null,
  secondary: null
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function isCameraBusyError(error: unknown): boolean {
  const s = error instanceof Error ? error.message : String(error)
  return (
    /device\s*busy/i.test(s) ||
    /ptp.*busy/i.test(s) ||
    /\b0x2019\b/i.test(s) ||
    /2333736/i.test(s) ||
    /2249016/i.test(s)
  )
}

async function setConfigValueWithRetry(
  cameraKey: CameraKey,
  name: string,
  value: string | number | boolean,
  maxRetries = 3
): Promise<void> {
  let lastError: unknown
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      await unifiedCameraUtils.setConfigValue(cameraKey, name, value)
      return
    } catch (error) {
      lastError = error
      if (!isCameraBusyError(error) || attempt === maxRetries) {
        throw error
      }
      const delay = 250 * (attempt + 1)
      console.warn(
        `[CameraSettingsManager] Camera busy while setting ${name}, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1})`
      )
      await sleep(delay)
    }
  }
  throw lastError instanceof Error ? lastError : new Error(String(lastError))
}

/**
 * Recursively search config tree for a setting by name patterns
 */
function findConfigByName(
  config: Config | Record<string, Config> | unknown,
  namePatterns: string[]
): Config | null {
  if (config == null) return null
  // `in` only accepts objects; gphoto/config trees may contain string leaves (e.g. ids/hashes).
  if (typeof config !== 'object') return null

  // Handle Record<string, Config> (children)
  if (!('name' in config)) {
    for (const key in config as Record<string, unknown>) {
      if (!Object.prototype.hasOwnProperty.call(config, key)) continue
      const found = findConfigByName((config as Record<string, unknown>)[key], namePatterns)
      if (found) return found
    }
    return null
  }

  // Handle Config object
  const configObj = config as Config
  if (typeof configObj.name !== 'string') return null
  const nameLower = configObj.name.toLowerCase()

  // Check if name matches any pattern
  for (const pattern of namePatterns) {
    if (nameLower.includes(pattern.toLowerCase())) {
      return configObj
    }
  }

  // Recursively search children
  if (configObj.children) {
    for (const key in configObj.children) {
      const found = findConfigByName(configObj.children[key], namePatterns)
      if (found) return found
    }
  }

  return null
}


/**
 * Detect available camera settings (shutter speed, exposure simulation)
 */
export async function detectAvailableSettings(cameraKey: CameraKey): Promise<AvailableSettings> {
  try {
    const config = await unifiedCameraUtils.getConfig(cameraKey)
    if (!config) {
      return { isSupported: false }
    }

    const configObj = config as Config

    // Find shutter speed setting
    const shutterSpeedConfig = findConfigByName(configObj, SHUTTER_SPEED_NAMES)
    const shutterSpeedName = shutterSpeedConfig?.name

    // Find exposure simulation setting
    const exposureSimConfig = findConfigByName(configObj, EXPOSURE_SIMULATION_NAMES)
    const exposureSimName = exposureSimConfig?.name

    const isSupported = !!shutterSpeedName || !!exposureSimName

    return {
      shutterSpeed: shutterSpeedName,
      exposureSimulation: exposureSimName,
      isSupported
    }
  } catch (error) {
    console.error('[CameraSettingsManager] Error detecting available settings:', error)
    return { isSupported: false }
  }
}

/**
 * Convert seconds to shutter speed string format (e.g., 0.033 -> "1/30")
 */
function formatShutterSpeed(seconds: number): string {
  // Find closest common shutter speed fraction
  const commonSpeeds = [
    1 / 4000, 1 / 2000, 1 / 1000, 1 / 500, 1 / 250, 1 / 200, 1 / 125, 1 / 60, 1 / 30, 1 / 15,
    1 / 8, 1 / 4, 1 / 2, 1, 2, 4, 8, 15, 30
  ]

  let closest = commonSpeeds[0]
  let minDiff = Math.abs(seconds - closest)

  for (const speed of commonSpeeds) {
    const diff = Math.abs(seconds - speed)
    if (diff < minDiff) {
      minDiff = diff
      closest = speed
    }
  }

  if (closest < 1) {
    const denominator = Math.round(1 / closest)
    return `1/${denominator}`
  } else {
    return String(Math.round(closest))
  }
}

/**
 * Validate shutter speed is within safe limits
 */
function validateShutterSpeed(seconds: number): boolean {
  // In seconds: larger = slower. Valid range is between 1/200s (fastest allowed)
  // and 1/4s (slowest allowed).
  return seconds >= MAX_FLASH_SYNC_SPEED && seconds <= MIN_SHUTTER_SPEED
}

/**
 * Save original camera settings before applying live view overrides
 */
export async function saveOriginalSettings(cameraKey: CameraKey): Promise<OriginalSettings | null> {
  try {
    const config = await unifiedCameraUtils.getConfig(cameraKey)
    if (!config) {
      console.warn('[CameraSettingsManager] No config available to save original settings')
      return null
    }

    const configObj = config as Config
    const original: OriginalSettings = {}

    // Find and save shutter speed
    const shutterSpeedConfig = findConfigByName(configObj, SHUTTER_SPEED_NAMES)
    if (shutterSpeedConfig && shutterSpeedConfig.value !== undefined) {
      original.shutterSpeed = {
        name: shutterSpeedConfig.name,
        value: shutterSpeedConfig.value
      }
    }

    // Find and save exposure simulation
    const exposureSimConfig = findConfigByName(configObj, EXPOSURE_SIMULATION_NAMES)
    if (exposureSimConfig && exposureSimConfig.value !== undefined) {
      original.exposureSimulation = {
        name: exposureSimConfig.name,
        value: exposureSimConfig.value
      }
    }

    if (!original.shutterSpeed && !original.exposureSimulation) {
      console.warn('[CameraSettingsManager] No relevant settings found to save')
      return null
    }

    console.log('[CameraSettingsManager] Saved original settings:', original)
    // Store in cache for later restoration
    originalSettingsCache[cameraKey] = original
    return original
  } catch (error) {
    console.error('[CameraSettingsManager] Error saving original settings:', error)
    return null
  }
}

/**
 * Apply live view brightness settings (slower shutter speed, disable exposure simulation)
 */
export async function applyLiveViewBrightness(
  cameraKey: CameraKey,
  settings: {
    autoBrightenLiveViewForFlash: boolean
    liveViewShutterSpeedOverride?: number
  }
): Promise<boolean> {
  try {
    if (!settings.autoBrightenLiveViewForFlash) {
      return false
    }

    // Check if camera is a real camera (not webcam)
    const store = useCameraStore.getState()
    const isPrimary = cameraKey === 'primary'
    const deviceType = isPrimary ? store.deviceType : store.secondaryDeviceType

    if (deviceType === 'webcam') {
      console.log('[CameraSettingsManager] Webcam detected, skipping live view brightness adjustment')
      return false
    }

    // Detect available settings
    const available = await detectAvailableSettings(cameraKey)
    if (!available.isSupported) {
      console.warn('[CameraSettingsManager] Camera does not support live view brightness adjustment')
      return false
    }

    let success = false

    // Apply shutter speed override
    if (available.shutterSpeed) {
      const overrideSpeed = settings.liveViewShutterSpeedOverride ?? DEFAULT_LIVE_VIEW_SHUTTER_SPEED

      // Validate shutter speed
      if (!validateShutterSpeed(overrideSpeed)) {
        console.warn(
          `[CameraSettingsManager] Invalid shutter speed ${overrideSpeed}s, clamping to safe range`
        )
        const clampedSpeed = Math.min(
          MIN_SHUTTER_SPEED,
          Math.max(MAX_FLASH_SYNC_SPEED, overrideSpeed)
        )
        const formattedSpeed = formatShutterSpeed(clampedSpeed)
        try {
          await setConfigValueWithRetry(cameraKey, available.shutterSpeed, formattedSpeed)
          success = true
          console.log(
            `[CameraSettingsManager] Applied shutter speed override: ${formattedSpeed} (${clampedSpeed}s)`
          )
        } catch (error) {
          console.error('[CameraSettingsManager] Failed to set shutter speed:', error)
        }
      } else {
        const formattedSpeed = formatShutterSpeed(overrideSpeed)
        try {
          await setConfigValueWithRetry(cameraKey, available.shutterSpeed, formattedSpeed)
          success = true
          console.log(
            `[CameraSettingsManager] Applied shutter speed override: ${formattedSpeed} (${overrideSpeed}s)`
          )
        } catch (error) {
          console.error('[CameraSettingsManager] Failed to set shutter speed:', error)
        }
      }
    }

    // Disable exposure simulation if available
    if (available.exposureSimulation) {
      try {
        // Try common values for disabling exposure simulation
        // This varies by camera - try false, 0, or "Off"
        const disableValues = [false, 0, 'Off', 'off', 'OFF']
        let exposureSimDisabled = false

        for (const value of disableValues) {
          try {
            await setConfigValueWithRetry(cameraKey, available.exposureSimulation, value)
            exposureSimDisabled = true
            console.log(
              `[CameraSettingsManager] Disabled exposure simulation with value: ${value}`
            )
            break
          } catch (error) {
            console.error('[CameraSettingsManager] Failed to disable exposure simulation:', error)
            // Try next value
            continue
          }
        }

        if (!exposureSimDisabled) {
          console.warn(
            '[CameraSettingsManager] Could not disable exposure simulation (tried common values)'
          )
        }
      } catch (error) {
        console.error('[CameraSettingsManager] Failed to disable exposure simulation:', error)
      }
    }

    return success
  } catch (error) {
    console.error('[CameraSettingsManager] Error applying live view brightness:', error)
    return false
  }
}

/**
 * Get cached original settings for a camera
 */
export function getCachedOriginalSettings(cameraKey: CameraKey): OriginalSettings | null {
  return originalSettingsCache[cameraKey] || null
}

/**
 * Clear cached original settings for a camera
 */
export function clearCachedOriginalSettings(cameraKey: CameraKey): void {
  originalSettingsCache[cameraKey] = null
}

/**
 * Restore original camera settings before capture
 */
export async function restoreOriginalSettings(
  cameraKey: CameraKey,
  originalSettings?: OriginalSettings | null
): Promise<void> {
  // Use provided settings or get from cache
  const settingsToRestore = originalSettings ?? originalSettingsCache[cameraKey]

  if (!settingsToRestore) {
    console.warn('[CameraSettingsManager] No original settings to restore')
    return
  }

  try {
    // Restore shutter speed
    if (settingsToRestore.shutterSpeed) {
      try {
        await unifiedCameraUtils.setConfigValue(
          cameraKey,
          settingsToRestore.shutterSpeed.name,
          settingsToRestore.shutterSpeed.value
        )
        console.log(
          `[CameraSettingsManager] Restored shutter speed: ${settingsToRestore.shutterSpeed.value}`
        )
      } catch (error) {
        console.error('[CameraSettingsManager] Failed to restore shutter speed:', error)
      }
    }

    // Restore exposure simulation
    if (settingsToRestore.exposureSimulation) {
      try {
        await unifiedCameraUtils.setConfigValue(
          cameraKey,
          settingsToRestore.exposureSimulation.name,
          settingsToRestore.exposureSimulation.value
        )
        console.log(
          `[CameraSettingsManager] Restored exposure simulation: ${settingsToRestore.exposureSimulation.value}`
        )
      } catch (error) {
        console.error('[CameraSettingsManager] Failed to restore exposure simulation:', error)
      }
    }

    // Clear cache after successful restoration (will be re-saved if needed)
    // Don't clear if this was called without originalSettings (might be called multiple times)
    if (originalSettings) {
      originalSettingsCache[cameraKey] = null
    }
  } catch (error) {
    console.error('[CameraSettingsManager] Error restoring original settings:', error)
  }
}

